import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Shield, Search, Activity, Zap } from 'lucide-react';
import { healthAPI } from './api/api';
import InstitutionDashboard from './components/InstitutionDashboard';
import StudentDashboard from './components/StudentDashboard';
import VerifierDashboard from './components/VerifierDashboard';

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState('institution');
  const [systemHealth, setSystemHealth] = useState({ status: 'checking', responseTime: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Check backend connection
    const checkHealth = async () => {
      const startTime = performance.now();
      try {
        const response = await healthAPI.check();
        const endTime = performance.now();
        console.log('Health check response:', response);
        setSystemHealth({
          status: 'healthy',
          responseTime: Math.round(endTime - startTime)
        });
      } catch (error: any) {
        setSystemHealth({
          status: 'error',
          responseTime: 0
        });
      }
    };

    checkHealth();
    
    // Set loaded after small delay for animations
    setTimeout(() => setIsLoaded(true), 1000);
  }, []);

  const tabs = [
    { 
      id: 'institution', 
      name: 'Institution', 
      icon: GraduationCap, 
      emoji: '🏛️', 
      component: InstitutionDashboard,
      color: 'from-neon-blue to-blue-500' 
    },
    { 
      id: 'student', 
      name: 'Student', 
      icon: Shield, 
      emoji: '👨‍🎓', 
      component: StudentDashboard,
      color: 'from-neon-green to-green-500'
    },
    { 
      id: 'verifier', 
      name: 'Verifier', 
      icon: Search, 
      emoji: '🔍', 
      component: VerifierDashboard,
      color: 'from-neon-purple to-purple-500'
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || InstitutionDashboard;

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-bg via-dark-card to-dark-surface flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="w-24 h-24 border-4 border-neon-blue/30 border-t-neon-blue rounded-full mx-auto mb-8"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <h2 className="text-4xl font-bold bg-gradient-to-r from-neon-blue to-neon-purple bg-clip-text text-transparent mb-4">Loading BlockCred</h2>
          <p className="text-xl text-gray-400">Initializing 3D blockchain verification system...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-bg via-dark-card to-dark-surface">
      {/* Header */}
      <motion.header 
        className="glass-card border-b border-neon-blue/20 sticky top-0 z-50"
        initial={{ opacity: 0, y: -100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <motion.div 
              className="flex items-center space-x-4"
              whileHover={{ scale: 1.02 }}
            >
              <motion.div
                className="p-3 bg-gradient-to-br from-neon-blue/20 to-neon-purple/20 rounded-xl"
                animate={{ 
                  boxShadow: [
                    "0 0 0 0 rgba(0, 212, 255, 0.4)",
                    "0 0 0 15px rgba(0, 212, 255, 0)",
                    "0 0 0 0 rgba(0, 212, 255, 0)"
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <GraduationCap className="h-8 w-8 text-neon-blue" />
              </motion.div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-neon-blue to-neon-purple bg-clip-text text-transparent">BlockCred</h1>
                <p className="text-sm text-gray-400">Academic Verification System v2.0</p>
              </div>
            </motion.div>
            
            <div className="flex items-center space-x-6">
              <motion.div 
                className="flex items-center space-x-2 px-4 py-2 glass-card rounded-lg"
                whileHover={{ scale: 1.05 }}
              >
                <Activity className={`h-4 w-4 ${
                  systemHealth.status === 'healthy' ? 'text-neon-green animate-pulse' : 
                  systemHealth.status === 'checking' ? 'text-yellow-400' : 'text-red-400'
                }`} />
                <span className="text-sm text-white font-medium">
                  {systemHealth.status === 'healthy' ? `✅ Connected (${systemHealth.responseTime}ms)` : 
                   systemHealth.status === 'checking' ? '🟡 Connecting...' : '❌ Connection Error'}
                </span>
              </motion.div>
              
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-neon-green">CST-2, Final Year B.Tech</p>
                <p className="text-xs text-gray-400 flex items-center">
                  <Zap className="h-3 w-3 mr-1" />
                  GPREC - Optimized Performance
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Navigation */}
      <motion.nav 
        className="glass-card border-b border-neon-blue/10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-0">
            {tabs.map((tab, index) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center space-x-3 py-6 px-8 transition-all duration-500 ${
                  activeTab === tab.id ? 'text-white' : 'text-gray-400 hover:text-white'
                }`}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.4 }}
              >
                {activeTab === tab.id && (
                  <motion.div
                    className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${tab.color}`}
                    layoutId="activeTabIndicator"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                
                <motion.div
                  animate={{ 
                    rotateY: activeTab === tab.id ? [0, 360] : 0,
                    scale: activeTab === tab.id ? 1.1 : 1
                  }}
                  transition={{ duration: activeTab === tab.id ? 0.6 : 0.3 }}
                >
                  <tab.icon className="h-6 w-6" />
                </motion.div>
                
                <span className="font-semibold text-xl">
                  {tab.emoji} {tab.name}
                </span>

                {activeTab === tab.id && (
                  <motion.div
                    className={`absolute inset-0 bg-gradient-to-r ${tab.color} opacity-10 rounded-lg`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.1 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.nav>

      {/* Main Content */}
      <main className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: activeTab === 'student' ? -100 : activeTab === 'verifier' ? 100 : 0, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: activeTab === 'student' ? 100 : activeTab === 'verifier' ? -100 : 0, y: -20 }}
            transition={{ 
              type: "spring", 
              stiffness: 100, 
              damping: 20,
              duration: 0.6 
            }}
          >
            <ActiveComponent />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default App;
